/**
 * Interpolation roots a source type computes for one declaration (for
 * example sequence anchors derived from form state). They ride on the
 * interpolation context like the built-in roots, so the readiness check,
 * the cache key, and the fetch all read the same values. A root that is
 * present but has no value at a path is a value, not a source that has
 * not loaded yet.
 */
const DERIVED_ROOTS = Symbol('derived-interpolation-roots');

export function isDerivedRoot(context: unknown, name: string): boolean {
  if (context === null || typeof context !== 'object') return false;
  const names = (context as Record<symbol, unknown>)[DERIVED_ROOTS];
  return Array.isArray(names) && names.indexOf(name) !== -1;
}

/**
 * Write `roots` onto a context the caller owns and mark them derived.
 * Returns the names that replaced an existing key.
 */
export function assignDerivedRoots(
  context: Record<string, unknown>,
  roots: Readonly<Record<string, unknown>>,
): string[] {
  const replaced: string[] = [];
  const names = readDerivedNames(context);
  for (const name of Object.keys(roots)) {
    if (
      Object.prototype.hasOwnProperty.call(context, name) &&
      names.indexOf(name) === -1
    ) {
      replaced.push(name);
    }
    context[name] = roots[name];
    if (names.indexOf(name) === -1) names.push(name);
  }
  Object.defineProperty(context, DERIVED_ROOTS, {
    value: names,
    enumerable: false,
    configurable: true,
  });
  return replaced;
}

/**
 * Copy of `context` with `roots` added. Symbol-keyed markers (published
 * scope, injected route, endpoint exclusions) are carried over so the copy
 * interpolates the same way the original does.
 */
export function withDerivedRoots(
  context: unknown,
  roots: Readonly<Record<string, unknown>>,
): Record<string, unknown> {
  const copy: Record<string, unknown> =
    context !== null && typeof context === 'object' && !Array.isArray(context)
      ? { ...(context as Record<string, unknown>) }
      : {};
  if (context !== null && typeof context === 'object') {
    for (const symbol of Object.getOwnPropertySymbols(context)) {
      if (symbol === DERIVED_ROOTS) continue;
      const descriptor = Object.getOwnPropertyDescriptor(context, symbol);
      if (descriptor) Object.defineProperty(copy, symbol, descriptor);
    }
    const inherited = readDerivedNames(context as Record<string, unknown>);
    if (inherited.length > 0) {
      Object.defineProperty(copy, DERIVED_ROOTS, {
        value: inherited,
        enumerable: false,
        configurable: true,
      });
    }
  }
  assignDerivedRoots(copy, roots);
  return copy;
}

function readDerivedNames(context: Record<string, unknown>): string[] {
  const names = (context as Record<symbol, unknown>)[DERIVED_ROOTS];
  return Array.isArray(names) ? [...(names as string[])] : [];
}
